import {cdnImage} from '@/util/cdnImage';
import AggregateScript from '@/components/Common/Aggregate-script';
import {MUIButton, MUIContainer} from '@/components/MUI-Components';
import {HIRE_AI_DEVELOPERS_META_DATA} from '@/util/metaData/hire';
import dynamic from 'next/dynamic';
import PageHead from '@/components/PageHead';
import {colors} from '@/styles/tokens';

const defiBannerBottomWrapperSx = {
    padding: '20px 0 !important'
};

// Preserved from the page's former hireAiDevelopers.module.scss — Pricing has
// no sx passthrough for its internal nodes, so the overrides stay scoped to
// this page's own Pricing call via `&&`, no component change needed.
const hireAiDevPricingGridSx = {
    '&& .hire-ai-dev-pricing .head-heigth .pricing-sub-heading': {textAlign: 'center !important'},
    '&& .hire-ai-dev-pricing .pricing-duration': {fontSize: '25px !important'},
    '@media (max-width: 1199px)': {
        '&& .hire-ai-dev-pricing .pricing-button': {width: '100% !important', padding: '10px 10px !important'}
    },
    '@media (max-width: 600px)': {
        '&& .hire-ai-dev-pricing .mobile-top-padding': {paddingTop: '0px !important'}
    }
} as const;

// ─── getStaticProps ─────────────────────────────────────────────────────────
export const getStaticProps = async () => {
    const {
        hireAiDevelopmentFAQValue,
        hireAiPricing,
        hireAiDevBannerPoints,
        hireAIDevCardTechnology,
        hireAiOutcomes,
        hireAiDevelopmentProcess,
        hireAiDevelopmentChooseBody,
        hireAiDevelopmentChooseContent,
        hireAiDeveloperEngineer,
        hireAiDeveloperIndustryData,
        legalChatbotCheckPoint,
        legalChatbotButtonData,
        aiAutomationERMCheckPoint,
        aiAutomationERMButtonData,
        aiInventoryCheckPoint,
        aiInventoryButtonData
    } = await import('@/util/contents/hire-ai-developers');

    const {whiteLabelBannerAwards, whiteLabelMobileAwards} = await import('@/util/contents/white-label-exchange');

    const {remoteHireBannerBottom} = await import('@/util/contents/remote-developer-data');

    return {
        props: {
            hireAiDevelopmentFAQValue,
            hireAiPricing,
            hireAiDevBannerPoints,
            hireAIDevCardTechnology,
            hireAiOutcomes,
            hireAiDevelopmentProcess,
            hireAiDevelopmentChooseBody,
            hireAiDevelopmentChooseContent,
            hireAiDeveloperEngineer,
            hireAiDeveloperIndustryData,
            legalChatbotCheckPoint,
            legalChatbotButtonData,
            aiAutomationERMCheckPoint,
            aiAutomationERMButtonData,
            aiInventoryCheckPoint,
            aiInventoryButtonData,
            whiteLabelBannerAwards,
            whiteLabelMobileAwards,
            remoteHireBannerBottom
        }
    };
};

const LatestInsights = dynamic(() => import('@/components/Ui-components/latest-insights'));
const Testimonials = dynamic(() => import('@/components/Ui-components/Testimonials'));
const Banner = dynamic(() => import('@/components/Ui-components/Banner'));
const Questions = dynamic(() => import('@/components/Ui-components/questions'));
const ClarityScript = dynamic(() => import('@/components/Common/clarity-script'));
const Pricing = dynamic(() => import('@/components/Ui-components/pricing'));
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
const DisplayImage = dynamic(() => import('@/components/Ui-components/display-image'));
const DynamicScreens = dynamic(() => import('@/components/Ui-components/Dynamic-screens'));
const TabTechnology = dynamic(() => import('@/components/Ui-components/TabTechnology'));
const LatestCTA = dynamic(() => import('@/components/Ui-components/LatestCta'));
import DoneIcon from '@mui/icons-material/Done';
const projectSuccess = cdnImage('hire-ai-developers/AI-Engineers.webp');
const HireProcess = dynamic(() => import('@/components/Ui-components/HireProcess'));
const OurMakeChoice = dynamic(() => import('@/components/Ui-components/OurMakeChoice'));
const lummid = cdnImage('ai/lumid-ai.webp');
const WebDeveloperProfile = dynamic(import('@/components/Ui-components/web-developer-profile'));
const ServiceTab = dynamic(() => import('@/components/Ui-components/ServiceTab'));
const advisoryAutomation = cdnImage('hire-ai-developers/Legal-Advisory-Automation.webp');
const commure = cdnImage('hire-ai-developers/Commure.webp');
import {meetingLink} from '@/constants';
import Link from 'next/link';
import {CDN_URL, SITE_URL} from '@/constants/urls';
const LetsDiscuss = dynamic(() => import('@/components/Ui-components/Lets-discuss'));

const HireAiDevelopers = (props: any) => {
    const {
        openForm,
        hireAiDevelopmentFAQValue,
        hireAiPricing,
        hireAiDevBannerPoints,
        hireAIDevCardTechnology,
        hireAiOutcomes,
        hireAiDevelopmentProcess,
        hireAiDevelopmentChooseBody,
        hireAiDevelopmentChooseContent,
        hireAiDeveloperEngineer,
        hireAiDeveloperIndustryData,
        legalChatbotCheckPoint,
        legalChatbotButtonData,
        aiAutomationERMCheckPoint,
        aiAutomationERMButtonData,
        aiInventoryCheckPoint,
        aiInventoryButtonData,
        whiteLabelBannerAwards,
        whiteLabelMobileAwards,
        remoteHireBannerBottom
    } = props;
    const mobileScreen = useMediaQuery('(max-width:480px)');

    return (
        <Box className="hire-ai-developers-main-parent">
            <PageHead meta={HIRE_AI_DEVELOPERS_META_DATA} url="/hire-ai-developers" />
            <Banner
                priority={true}
                handleClickOpen={openForm}
                heading="Hire AI Developers"
                subheading="Building AI solutions is complex—hiring the right talent shouldn’t be. Hire AI developers directly from us to streamline your resourcing strategy. Our talent pool includes top 3% AI engineers, pre-vetted for production environments and ready to deploy in days. You get scale, speed, and skill—without the overhead of traditional hiring."
                button="Schedule a Quick Call"
                image={cdnImage('hire-ai-developers/Hire-ai-developers.webp')}
                bannerClass="blockchain-banner justify-center hire-ai-developer-banner"
                alt="Hire AI Developers"
                DataPoints={hireAiDevBannerPoints}
                bannerIcons={mobileScreen ? whiteLabelMobileAwards : whiteLabelBannerAwards}
            />
            <Box className="sub-banner-wrapper defi-banner-bottom-wrapper" sx={defiBannerBottomWrapperSx}>
                <MUIContainer>
                    <Grid container spacing={2} alignItems="center" className="white-label-bottom-container">
                        <Grid item md={2} lg={1.7} sm="auto" xs={12}>
                            <Typography className="banner-bottom-heading">Our Strategic Alliances</Typography>
                        </Grid>
                        {remoteHireBannerBottom?.map((data: any, index: number) => (
                            <Grid item md={2} lg={1.7} sm="auto" xs={6} className="sub-banner-images" key={index + 1}>
                                <Box className="images-banner-bottom">
                                    <DisplayImage src={data.src} alt={data.alt} />
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </MUIContainer>
            </Box>
            <Grid item xs={12} className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding">
                <WebDeveloperProfile
                    heading="AI Development Talent You Can Count On"
                    subheading="Review the detailed profiles of our AI engineers to understand how their backgrounds align with your business goals."
                    contentData={hireAiDeveloperEngineer}
                />
                <Box mt={3} className="prompt-pricing-button app-bar-contact-btn-wrapper">
                    <MUIContainer>
                        <Link href={meetingLink} target="_blank">
                            <MUIButton label="Book a Quick Call" className="helper-card-button" />
                        </Link>
                    </MUIContainer>
                </Box>
            </Grid>

            <Box className="bottom-spacing  mobile-bottom-padding top-space mobile-top-padding">
                <LatestCTA
                    heading="Aspiring to Hire AI developers who understand your unique business challenges?"
                    subheading="We have a pool of talented AI developers skilled in designing and developing AI-based applications that enhance productivity and drive results. Let us help you create intelligent applications that deliver long-term value and ROI."
                    buttonText="Schedule a Developer Interview"
                    meetingLink={true}
                    openForm={meetingLink}
                    className="blue-background-cta"
                />
            </Box>
            <Grid item xs={12} className="bottom-spacing ">
                <MUIContainer>
                    <Grid container spacing={2}>
                        <Grid item md={6} xs={12}>
                            <Box className="spectrum-header-wrapper solutions trend-dominate-text-wrapper" pb={2}>
                                <Typography className="spectrum-heading color-black " component="h2" pb={2}>
                                    Enable Better Business Outcomes by Collaborating with Top 3% AI Engineers
                                </Typography>
                                <Typography className="spectrum-sub-heading color-black" component="p">
                                    Ride the AI wave confidently by partnering with the top 1% of vetted AI experts who deliver sustainable
                                    business values. Whether you aim to develop custom AI solutions or integrate cutting-edge neural
                                    networks into your business processes, our developers have the expertise to ensure scalable, measurable
                                    outcomes.
                                </Typography>
                            </Box>
                            <Box>
                                {hireAiOutcomes?.map((item: any, index: number) => (
                                    <Box className="done-icon-wrapper" key={index + 1}>
                                        <DoneIcon sx={{color: colors.text.orangeAccessible}} />
                                        <Box>
                                            <Typography className="done-subheading color-black">{item.subheading}</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <Box className="hire-prompt-tailored-solution text-center">
                                <DisplayImage src={projectSuccess} alt="Hire Prompt Engineering" />
                            </Box>
                        </Grid>
                    </Grid>
                </MUIContainer>
            </Grid>
            <Grid
                item
                xs={12}
                className="top-space bottom-spacing helper-section mobile-top-padding blue-service-container-wrapper mobile-bottom-padding  helper_section_parent"
            >
                <MUIContainer>
                    <ServiceTab
                        heading="Industries We Serve"
                        technology={hireAiDeveloperIndustryData}
                        className="blue-service-tab-card hire-web-developer-industry-tab"
                        tabWrapperClass="hire-web-developer-tab-wrapper"
                    />
                </MUIContainer>
            </Grid>
            <Box className="bottom-spacing  mobile-bottom-padding top-space mobile-top-padding">
                <LatestCTA
                    heading="From Roadmap to Rollout—We Build AI That Works in Your Domain"
                    subheading="Our AI teams understand the nuances of your industry—from compliance to user behavior. Let’s co-develop what’s next."
                    buttonText="Book a Discovery Call"
                    openForm={openForm}
                    className="blue-background-cta"
                />
            </Box>
            <Grid item xs={12} className="dynmaic-white-label-wrapper  bottom-margin dynamic-bep20-wrapper mobile-bottom-margin ">
                <MUIContainer>
                    <Box className="spectrum-header-wrapper solutions">
                        <Typography className="spectrum-heading color-black " component="h2">
                            Client Success Stories
                        </Typography>
                        <Box className="divder-wrapper">
                            <hr className="divider-line" />
                        </Box>
                    </Box>
                    <DynamicScreens
                        imageSrc={advisoryAutomation}
                        heading="AI-Powered Legal Chatbot for Streamlining Client Advisory"
                        subPoints={[
                            'A government-backed, U.S.-based legal advisory organization partnered with Debut Infotech to streamline immigration consultation services and reduce operational bottlenecks. The engagement was delivered in two phases, starting with a multilingual AI chatbot for eligibility screening, followed by deeper automation of document intake and analytics. Our team provided end-to-end development, from architecture to deployment, and the project remains ongoing.'
                        ]}
                        reverseOrder={true}
                        alt="AI-Powered Legal Chatbot"
                        checkItems={legalChatbotCheckPoint}
                        buttonData={legalChatbotButtonData}
                        buttonLabel="Schedule a Meeting"
                        buttonClass="nft-button"
                        openForm={openForm}
                    />
                    <DynamicScreens
                        imageSrc={commure}
                        heading="Clinical AI Automation for EMR-Integrated Healthcare Platform"
                        subPoints={[
                            'A leading healthcare platform in the U.S. partnered with Debut Infotech under a 3-year agreement to reduce documentation fatigue and enhance clinical productivity. Our developers continue to support their onshore team with AI automation focused on transcriptions, medical coding, and unified EMR access.'
                        ]}
                        reverseOrder={false}
                        alt="Clinical AI Automation"
                        checkItems={aiAutomationERMCheckPoint}
                        buttonData={aiAutomationERMButtonData}
                        imageClass="dynamic-exchange-image"
                        buttonLabel="Schedule a Meeting"
                        openForm={openForm}
                        buttonClass="helper-card-button"
                    />
                    <DynamicScreens
                        imageSrc={lummid}
                        heading="AI-Powered Data Engineering for Logistics Inventory Optimization"
                        subPoints={[
                            'A North American logistics provider engaged Debut Infotech to build a centralized AI platform for managing real-time container inventory. The goal was to replace manual tracking with intelligent automation and speed up sales operations. Delivered as a fixed-price MVP in 4 months, the solution improved visibility and streamlined internal workflows.'
                        ]}
                        reverseOrder={true}
                        alt="Saavi"
                        checkItems={aiInventoryCheckPoint}
                        buttonData={aiInventoryButtonData}
                        buttonLabel="Schedule a Meeting"
                        buttonClass="nft-button"
                        openForm={openForm}
                    />
                </MUIContainer>
            </Grid>
            <Grid item xs={12}>
                <HireProcess content={hireAiDevelopmentProcess} />
            </Grid>
            <Grid item xs={12} className="bottom-spacing mobile-bottom-padding" sx={hireAiDevPricingGridSx}>
                <Container maxWidth="lg">
                    <Pricing
                        heading="Our Agile Project Engagement Plans"
                        subheading="Our AI developers for hire possess in-depth technical knowledge to deliver high-quality AI solutions. With flexible T&M pricing plans, they provide personalized development experiences designed to meet your specific project requirements."
                        pricingData={hireAiPricing}
                        className="hire-ai-dev-pricing"
                        onClick={openForm}
                    />
                    <Box mt={2} className="prompt-pricing-button">
                        <MUIButton onClick={openForm} label="Share Your Project Requirements" className="helper-card-button" />
                    </Box>
                </Container>
            </Grid>
            <hr className="hr-line zero-margin" />
            <Grid item xs={12} className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <MUIContainer>
                    <TabTechnology
                        heading="Hire AI Developers Proficient in a Wide Range of AI Technologies"
                        subheading="Our team of expert AI developers brings unparalleled expertise in building and integrating AI-powered solutions tailored to your business needs. From AI-based automation to advanced <a target='_blank' href='/blog/machine-learning-model-training-guide'>machine learning models</a>, we deliver scalable, secure, and high-performance solutions. Hire remote AI developers from us and transform your business with cutting-edge AI innovations."
                        technology={hireAIDevCardTechnology}
                    />
                </MUIContainer>
            </Grid>
            <OurMakeChoice
                heading="Why Choose Us to Hire AI Developers?"
                body={hireAiDevelopmentChooseBody}
                contentData={hireAiDevelopmentChooseContent}
            />
            <Grid item xs={12}>
                <LetsDiscuss
                    heading="Struggling to Find the Right AI Talent for Your Project?"
                    subheading="Let’s collaborate to deliver innovative, high-impact AI solutions tailored to your business needs."
                    btnLabel="Drop Us a Note Today"
                    openForm={() => {
                        globalThis.open('/contact-us', '_blank');
                    }}
                />
            </Grid>
            <Box className="question-parent margin top-space mobile-top-padding">
                <MUIContainer>
                    <Questions accordianData={hireAiDevelopmentFAQValue} subpart={true} />
                </MUIContainer>
            </Box>
            <Grid item xs={12}>
                <Box className="mobile-top-padding top-space bg-blue-background">
                    <Testimonials
                        heading="Inspiring Client Stories So Far"
                        subheading="Explore a compelling narrative of success with our inspiring client stories. From pioneering projects to transformative partnerships, our tech-forward blockchain solutions have propelled unparalleled growth. Dive into a showcase of innovation and achievement, highlighting the impactful journeys we've undertaken so far."
                    />
                </Box>
            </Grid>
            <Grid item xs={12} className="latest-insights mobile-top-padding top-space">
                <LatestInsights id="21" />
            </Grid>
            <ClarityScript clarityId="n5j87tyulh" />
            <script
                id="hire-ai-developers"
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: [
                            {
                                '@type': 'Question',
                                name: 'What are artificial intelligence developers?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'AI developers are software engineers who build AI models, algorithms, and systems to replicate human intelligence. They specialize in machine learning, natural language processing (NLP), computer vision, and more to solve complex business problems using data and advanced programming.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What is the cost to hire AI developers?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'The cost to hire AI developers varies based on project complexity, required expertise, and development duration. Typically, hiring an AI developer can range from $30 to $150 per hour or $5,000 to $25,000 per month. Get a custom estimate by sharing your project details with us.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Can I hire top Artificial Intelligence engineers from you to integrate AI into my existing app?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Yes, you can hire AI developers with experience in integrating AI technologies such as predictive analytics, recommendation engines, and NLP into existing applications. We ensure smooth integration and enhanced functionality without disrupting ongoing operations.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What services do your AI developers provide?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: `Our AI developers offer comprehensive services, including:
Custom AI model development
AI-driven mobile/web application development
Natural Language Processing (NLP) integration
Predictive analytics and forecasting
AI-powered chatbots
Generative AI solutions
AI-based security and fraud detection`
                                }
                            },
                            {
                                '@type': 'Question',
                                name: "How do you ensure the quality of your AI developers' work?",
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: `We ensure high-quality development by:
Conducting regular code reviews
Following best development practices
Implementing automated and manual testing
Maintaining transparent feedback loops
Complying with industry security and coding standards`
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How does hiring AI developers benefit my business?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: `Hiring skilled AI developers helps businesses:
Automate repetitive tasks
Personalize user experiences
Improve decision-making through data-driven insights
Accelerate time-to-market
Optimize processes and boost operational efficiency`
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What skills should I look for in AI engineers?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: `Look for AI engineers with expertise in:
Programming languages (Python, R, Java, C++)
AI frameworks (TensorFlow, PyTorch, Scikit-learn)
Machine learning and deep learning
Data processing and modeling
NLP and computer vision
Cloud platforms (AWS, Azure, GCP)`
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Can I hire remote AI developers?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Yes, Debut Infotech offers remote AI developers who work seamlessly across time zones and provide regular updates to ensure project success. Remote developers can be hired for short-term or long-term projects based on your needs.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How do AI developers differ from LLM developers?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'AI developers work on various AI applications, including ML models, NLP, and computer vision. LLM (Large Language Model) developers focus specifically on building and optimizing language models such as GPT for text processing, conversational AI, and content generation.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'How long does it take to develop an AI-based solution?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: `Development timelines vary depending on project complexity and scope.
Basic AI solutions: 4-12 weeks
Enterprise-level AI applications: 6-12 months
We provide realistic timelines after analyzing your project requirements.`
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'Do you offer AI consulting before development begins?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Yes, we provide AI consulting to help businesses define project requirements, assess data readiness, and create a development roadmap. Our AI consultants ensure that your solution delivers measurable value.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What is the difference between hiring freelance AI developers and dedicated AI teams?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'Freelancers offer flexibility for short-term tasks, while dedicated AI teams provide end-to-end project management, scalability, and long-term support, ensuring consistent quality and faster delivery.'
                                }
                            },
                            {
                                '@type': 'Question',
                                name: 'What support tools do you use to manage remote AI projects?',
                                acceptedAnswer: {
                                    '@type': 'Answer',
                                    text: 'We use project management tools like Jira, Slack, Trello, and Asana to ensure effective collaboration, progress tracking, and timely updates across remote teams.'
                                }
                            }
                        ]
                    })
                }}
            />
            <AggregateScript
                name="Hire AI Developers"
                image={`${CDN_URL}?url=%2Fimages%2Fhire-ai-developers%2FHire-ai-developers.webp&w=750&q=75`}
                description="Empower your projects by hiring expert AI developers. Our skilled team specializes in creating tailored AI solutions, enhancing automation, data analysis, and user experiences to drive innovation and business growth."
                offers={{
                    '@type': 'AggregateOffer',
                    url: `${SITE_URL}/hire-ai-developers`,
                    priceCurrency: 'USD',
                    lowPrice: '5000',
                    highPrice: '200000',
                    offerCount: '5'
                }}
                aggregateRating={{
                    '@type': 'AggregateRating',
                    ratingValue: '4.7',
                    bestRating: '5',
                    worstRating: '2',
                    ratingCount: '399'
                }}
            />
        </Box>
    );
};

export default HireAiDevelopers;
HireAiDevelopers.Layout = 'Main';
